import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { IoChevronDownOutline } from 'react-icons/io5';
import { useAuth } from '../../contexts/AuthContext';
import { BIBLE_BOOKS } from '../../utils/bibleBooks';
import { getLastBook, setLastBook } from '../../utils/localPreferences';
import ReadingSheet from '../reading/ReadingSheet';
import './ChapterCalendarSelector.css';

const idsFor = (book, from = 1, to = book.chapters) => {
  const ids = [];
  for (let n = from; n <= to; n++) ids.push(book.startId + n - 1);
  return ids;
};

const addIds = (selected, ids) => [...new Set([...selected, ...ids])];

/**
 * Chapters of one book at a time, picked by tap or (on desktop) drag.
 * Selection carries across books. Ids come from BIBLE_BOOKS, so there is
 * nothing to load.
 */
const ChapterCalendarSelector = ({ selectedChapters, onChaptersChange }) => {
  const { user } = useAuth();
  const [bookIndex, setBookIndex] = useState(0);
  const [showBooks, setShowBooks] = useState(false);
  const [search, setSearch] = useState('');
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [isTouch, setIsTouch] = useState(false);
  const [rangeMode, setRangeMode] = useState(false);
  const [anchor, setAnchor] = useState(null);

  useEffect(() => {
    const check = () => setIsTouch(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Pick up where they left off rather than resetting to Genesis every visit.
  useEffect(() => {
    const remembered = getLastBook(user?.id);
    const index = BIBLE_BOOKS.findIndex(b => b.name === remembered);
    if (index >= 0) setBookIndex(index);
  }, [user?.id]);

  const book = BIBLE_BOOKS[bookIndex];
  const selectedSet = useMemo(() => new Set(selectedChapters), [selectedChapters]);
  const selectedInBook = idsFor(book).filter(id => selectedSet.has(id)).length;

  const countIn = (b) => {
    let count = 0;
    for (let id = b.startId; id < b.startId + b.chapters; id++) if (selectedSet.has(id)) count++;
    return count;
  };

  const chooseBook = (index) => {
    setBookIndex(index);
    setLastBook(user?.id, BIBLE_BOOKS[index].name);
    setShowBooks(false);
    setSearch('');
    setDragStart(null);
    setDragEnd(null);
    setAnchor(null);
  };

  const fillRange = (a, b) => {
    const from = Math.min(a, b);
    const to = Math.max(a, b);
    onChaptersChange(addIds(selectedChapters, idsFor(book, from, to)));
  };

  const toggle = (n) => {
    const id = book.startId + n - 1;
    onChaptersChange(
      selectedSet.has(id) ? selectedChapters.filter(x => x !== id) : [...selectedChapters, id]
    );
  };

  const handleClick = (event, n) => {
    event.preventDefault();
    if (rangeMode) {
      if (anchor === null) {
        setAnchor(n);
      } else {
        fillRange(anchor, n);
        setAnchor(null);
        setRangeMode(false);
      }
      return;
    }
    if (event.shiftKey && anchor !== null) {
      fillRange(anchor, n);
      return;
    }
    setAnchor(n);
    toggle(n);
  };

  // Desktop drag adds a run of chapters in one gesture.
  const handleMouseDown = (event, n) => {
    if (isTouch || event.shiftKey) return;
    event.preventDefault();
    setDragStart(n);
    setDragEnd(n);
  };
  const handleMouseEnter = (n) => {
    if (dragStart !== null) setDragEnd(n);
  };
  const endDrag = () => {
    if (dragStart !== null && dragEnd !== null && dragStart !== dragEnd) {
      fillRange(dragStart, dragEnd);
      setAnchor(dragEnd);
    }
    setDragStart(null);
    setDragEnd(null);
  };

  const inDrag = (n) => (
    dragStart !== null && dragEnd !== null &&
    n >= Math.min(dragStart, dragEnd) && n <= Math.max(dragStart, dragEnd)
  );

  const matches = BIBLE_BOOKS
    .map((b, index) => ({ b, index }))
    .filter(({ b }) => b.name.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <div className="chapter-picker">
      <div className="chapter-picker-bar">
        <button type="button" className="chapter-picker-book" onClick={() => setShowBooks(true)}>
          <span className="chapter-picker-book-name">{book.name}</span>
          <span className="chapter-picker-book-meta">
            {selectedInBook > 0 ? `${selectedInBook}/${book.chapters}` : `${book.chapters} ch`}
          </span>
          <IoChevronDownOutline size={16} aria-hidden="true" />
        </button>
        <div className="chapter-picker-actions">
          {isTouch && (
            <button
              type="button"
              className={`chapter-picker-link ${rangeMode ? 'active' : ''}`}
              onClick={() => { setRangeMode(!rangeMode); setAnchor(null); }}
              aria-pressed={rangeMode}
            >
              Range
            </button>
          )}
          <button
            type="button"
            className="chapter-picker-link"
            onClick={() => onChaptersChange(addIds(selectedChapters, idsFor(book)))}
          >
            All
          </button>
          <button
            type="button"
            className="chapter-picker-link"
            onClick={() => { onChaptersChange([]); setAnchor(null); }}
            disabled={selectedChapters.length === 0}
          >
            Clear
          </button>
        </div>
      </div>

      {rangeMode && (
        <p className="chapter-picker-hint" role="status">
          {anchor === null ? 'Tap the first chapter.' : `From ${anchor}. Tap the last chapter.`}
        </p>
      )}

      <div
        className="chapter-grid"
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        role="group"
        aria-label={`${book.name} chapters`}
      >
        {Array.from({ length: book.chapters }, (_, i) => i + 1).map(n => {
          const selected = selectedSet.has(book.startId + n - 1);
          return (
            <button
              key={n}
              type="button"
              className={`chapter-cell ${selected ? 'selected' : ''} ${inDrag(n) ? 'in-range' : ''} ${anchor === n && rangeMode ? 'anchor' : ''}`}
              aria-pressed={selected}
              aria-label={`${book.name} ${n}`}
              onMouseDown={(e) => handleMouseDown(e, n)}
              onMouseEnter={() => handleMouseEnter(n)}
              onClick={(e) => handleClick(e, n)}
            >
              {n}
            </button>
          );
        })}
      </div>

      <ReadingSheet open={showBooks} onClose={() => { setShowBooks(false); setSearch(''); }} title="Choose a book">
        <input
          type="search"
          className="chapter-picker-search"
          placeholder="Search books"
          aria-label="Search books"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <ul className="chapter-picker-books">
          {matches.map(({ b, index }) => {
            const count = countIn(b);
            return (
              <li key={b.name}>
                <button
                  type="button"
                  className={`chapter-picker-option ${index === bookIndex ? 'active' : ''}`}
                  onClick={() => chooseBook(index)}
                >
                  <span>{b.name}</span>
                  <small>{count > 0 ? `${count} selected` : `${b.chapters} ch`}</small>
                </button>
              </li>
            );
          })}
          {matches.length === 0 && <li className="chapter-picker-empty">No book matches “{search}”.</li>}
        </ul>
      </ReadingSheet>
    </div>
  );
};

ChapterCalendarSelector.propTypes = {
  selectedChapters: PropTypes.arrayOf(PropTypes.number).isRequired,
  onChaptersChange: PropTypes.func.isRequired
};

export default ChapterCalendarSelector;
